import { cellCounts, type gridMath } from "./grid";

type Point = { x: number; y: number };
type Piece = Point & { size?: number };
type Bounds = { width_px: number; height_px: number };

// The cell of the map nearest the target where a token of `size` cells overlaps no other token: its centre, from the grid's own
// cells. A token's box is its size in cells wide by its size in cells tall, so two boxes overlap when their centres are nearer than
// half the sum of their sides on both axes. With no free cell the nearest one stacks; either way the spot is on the map.
export function freeSpot(math: ReturnType<typeof gridMath>, target: Point, pieces: Piece[], bounds: Bounds, size = 1): Point {
	const { grid } = math;
	const [width, height] = [size * grid.cell_w, size * grid.cell_h];
	const overlaps = (at: Point, piece: Piece) => {
		const other = piece.size ?? 1;
		return Math.abs(at.x - piece.x) < (width + other * grid.cell_w) / 2 - 1e-6 && Math.abs(at.y - piece.y) < (height + other * grid.cell_h) / 2 - 1e-6;
	};
	const { cols, rows } = cellCounts(grid, bounds.width_px, bounds.height_px);
	const spots: { at: Point; away: number; free: boolean }[] = [];
	for (let col = 0; col <= cols; col++) {
		for (let row = 0; row <= rows; row++) {
			const at = math.centerOf(col, row);
			if (at.x <= 0 || at.y <= 0 || at.x >= bounds.width_px || at.y >= bounds.height_px) continue;
			spots.push({ at, away: Math.hypot(at.x - target.x, at.y - target.y), free: !pieces.some((piece) => overlaps(at, piece)) });
		}
	}
	const nearest = (list: typeof spots) => list.reduce<(typeof spots)[number] | null>((best, spot) => (!best || spot.away < best.away ? spot : best), null);
	const found = nearest(spots.filter((spot) => spot.free)) ?? nearest(spots);
	return found?.at ?? { x: Math.min(Math.max(target.x, 0), bounds.width_px), y: Math.min(Math.max(target.y, 0), bounds.height_px) };
}

// The middle of the points, or null for none.
export function centroid(points: Point[]): Point | null {
	if (points.length === 0) return null;
	return { x: points.reduce((sum, point) => sum + point.x, 0) / points.length, y: points.reduce((sum, point) => sum + point.y, 0) / points.length };
}
